/*
 * Copyright 2026 North Carolina State University
 */

import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Paper,
  Stack,
  TextField,
  Typography
} from '@mui/material';
import { isValidInternationalPhone } from '../../utils/phoneUtils';
import PhoneInput from './PhoneInput';

// Accounts created through single sign-on are seeded with this literal instead of
// a real value, so it has to count as "not filled in".
const PLACEHOLDER = 'please update in profile';

const isBlank = (value) => {
  if (typeof value !== 'string') return !value;
  const trimmed = value.trim();
  return trimmed === '' || trimmed.toLowerCase() === PLACEHOLDER;
};

const REQUIRED_FIELDS = [
  {
    key: 'contact_number',
    label: 'Contact Number',
    phone: true,
    validate: (value) => (isValidInternationalPhone(value)
      ? null
      : 'Enter a valid phone number including country code')
  },
  {
    key: 'address',
    label: 'Address',
    placeholder: 'Street, City, State, ZIP',
    multiline: true,
    rows: 3,
    validate: (value) => (value.trim().length >= 10
      ? null
      : 'Enter your full address')
  }
];

/**
 * Blocks an application form until the signed-in user has a contact number and
 * address on file, because both are printed on the invoices generated from the
 * resulting project. Renders `children` untouched once the profile is complete.
 */
const ProfileCompletionGate = ({ children }) => {
  const [profile, setProfile] = useState(null);
  const [values, setValues] = useState({});
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [saving, setSaving] = useState(false);

  const loadProfile = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const { data } = await axios.get('/api/users/profile');
      setProfile(data);
      setValues({
        contact_number: isBlank(data.contact_number) ? '' : data.contact_number,
        address: isBlank(data.address) ? '' : data.address
      });
    } catch (err) {
      console.error('Error loading profile for application gate:', err);
      setLoadError('We could not check your profile. Please reload the page.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadProfile();
  }, [loadProfile]);

  const missingFields = profile
    ? REQUIRED_FIELDS.filter(field => isBlank(profile[field.key]))
    : [];

  const handleChange = (field, rawValue) => {
    const value = field.format ? field.format(rawValue) : rawValue;
    setValues(prev => ({ ...prev, [field.key]: value }));
    setErrors(prev => ({ ...prev, [field.key]: null }));
  };

  const handleSave = async () => {
    const nextErrors = {};
    missingFields.forEach(field => {
      const value = values[field.key] || '';
      const error = isBlank(value) ? `${field.label} is required` : field.validate(value);
      if (error) nextErrors[field.key] = error;
    });

    if (Object.keys(nextErrors).length > 0) {
      setErrors(nextErrors);
      return;
    }

    setSaving(true);
    try {
      // The profile endpoint replaces the fields it receives, so the whole
      // profile is sent back with only the missing values filled in. Facility is
      // flattened to its id because it arrives populated.
      const { data } = await axios.put('/api/users/profile', {
        ...profile,
        facility: profile.facility?._id || profile.facility || '',
        contact_number: values.contact_number,
        address: values.address
      });

      setProfile(data);
      toast.success('Profile updated. You can now fill out the application.');
    } catch (err) {
      console.error('Error saving profile from application gate:', err);
      toast.error(err.response?.data?.error || 'Failed to update your profile');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (loadError) {
    return <Alert severity="error" sx={{ my: 2 }}>{loadError}</Alert>;
  }

  if (missingFields.length === 0) {
    return children;
  }

  return (
    <Paper elevation={0} sx={{ p: 3, border: '1px solid #e2e8f0', borderRadius: 2, maxWidth: 620 }}>
      <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
        Complete your profile to continue
      </Typography>
      <Typography variant="body2" sx={{ color: '#64748b', mb: 3 }}>
        {missingFields.length === 1
          ? `Your ${missingFields[0].label.toLowerCase()} is missing.`
          : 'Your contact number and address are missing.'}
        {' '}
        Both appear on the invoices for this project, so they are needed before you
        can start an application. This is saved to your profile, so you only have to
        do it once.
      </Typography>

      <Stack spacing={2.5}>
        {missingFields.map(field => (
          field.phone ? (
            <Box key={field.key}>
              <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600 }}>
                {field.label}
              </Typography>
              <PhoneInput
                value={values[field.key] || ''}
                onChange={(v) => handleChange(field, v)}
                required
              />
              {errors[field.key] && (
                <Typography variant="caption" color="error">{errors[field.key]}</Typography>
              )}
            </Box>
          ) : (
          <TextField
            key={field.key}
            label={field.label}
            value={values[field.key] || ''}
            onChange={(e) => handleChange(field, e.target.value)}
            placeholder={field.placeholder}
            error={!!errors[field.key]}
            helperText={errors[field.key] || ' '}
            multiline={!!field.multiline}
            rows={field.rows}
            inputProps={field.maxLength ? { maxLength: field.maxLength } : undefined}
            fullWidth
            required
          />
          )
        ))}
      </Stack>

      <Box sx={{ display: 'flex', gap: 1.5, mt: 1 }}>
        <Button variant="contained" onClick={handleSave} disabled={saving}>
          {saving ? 'Saving...' : 'Save and Continue'}
        </Button>
        <Button variant="text" onClick={loadProfile} disabled={saving}>
          Refresh
        </Button>
      </Box>
    </Paper>
  );
};

export default ProfileCompletionGate;
